import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { ArrowRight, Loader2, Paperclip } from 'lucide-react';
import { clsx } from 'clsx';
import { useStreamingChat } from '../../contexts/StreamingChatContext';
import { useFileUpload } from '../../hooks/useFileUpload';
import VoiceInputButton from './VoiceInputButton';
import FilePreviewChips from './FilePreviewChips';
import toast from 'react-hot-toast';

export interface ChatInputHandle {
  setMessage: (text: string) => void;
}

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  agentId?: string;
  modelOverride?: string;
  enableCacheDiagnostics?: boolean;
}

const MIN_ROWS = 3;
const MAX_ROWS = 6;
const LINE_HEIGHT = 24;
const PADDING = 28;

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  ({ value, onChange, agentId, modelOverride, enableCacheDiagnostics }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { startChat, isStreaming } = useStreamingChat();

    const { pendingFiles, isUploading, addFiles, removeFile, uploadAll, clear } = useFileUpload();

    useImperativeHandle(ref, () => ({
      setMessage: (text: string) => {
        onChange(text);
        requestAnimationFrame(() => textareaRef.current?.focus());
      },
    }));

    const resize = useCallback(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      const minHeight = LINE_HEIGHT * MIN_ROWS + PADDING;
      const maxHeight = LINE_HEIGHT * MAX_ROWS + PADDING;
      const clamped = Math.max(minHeight, Math.min(el.scrollHeight, maxHeight));
      el.style.height = `${clamped}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, []);

    useEffect(() => {
      resize();
    }, [value, resize]);

    useEffect(() => {
      textareaRef.current?.focus();
    }, []);

    const handleSubmit = useCallback(async () => {
      const trimmed = value.trim();
      if ((!trimmed && pendingFiles.length === 0) || isStreaming || isUploading) return;

      try {
        let uploadIds: string[] | undefined;
        if (pendingFiles.length > 0) {
          uploadIds = await uploadAll();
        }

        onChange('');
        clear();
        await startChat(trimmed || '(file attached)', uploadIds, agentId, modelOverride, enableCacheDiagnostics);
      } catch (err) {
        onChange(value);
        toast.error(err instanceof Error ? err.message : 'Failed to start chat. Please try again.');
      }
    }, [value, isStreaming, isUploading, pendingFiles, startChat, onChange, uploadAll, clear, agentId, modelOverride, enableCacheDiagnostics]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const error = addFiles(e.target.files);
        if (error) toast.error(error);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const isBusy = isStreaming || isUploading;
    const canSend = (value.trim().length > 0 || pendingFiles.length > 0) && !isBusy;

    return (
      <div className="w-full max-w-[680px]">
        <div className="rounded-2xl border border-border bg-muted focus-within:border-primary focus-within:shadow-[0_0_0_3px_rgba(139,92,246,0.15)] transition-all duration-150 overflow-hidden">
          {pendingFiles.length > 0 && (
            <div className="border-b border-border dark:border-primary/20">
              <FilePreviewChips files={pendingFiles} onRemove={removeFile} disabled={isBusy} />
            </div>
          )}
          <textarea
            ref={textareaRef}
            rows={3}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about your business..."
            disabled={isBusy}
            className="w-full resize-none bg-transparent text-base text-foreground placeholder:text-muted-foreground px-4 pt-3.5 pb-2 focus:outline-none disabled:opacity-60"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf,.pdf,.csv,.txt,.docx,.xlsx"
            multiple
            className="hidden"
            onChange={handleFileSelect}
          />
          <div className="flex items-center justify-between gap-2 px-2 pb-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
              className={clsx(
                'flex items-center justify-center w-9 h-9 rounded-full',
                'text-muted-foreground hover:text-foreground',
                'hover:bg-accent transition-colors',
                'disabled:opacity-40 disabled:cursor-not-allowed'
              )}
              aria-label="Attach image"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1">
              <VoiceInputButton
                onTranscriptReady={(result) => {
                  onChange(result.transcript);
                  requestAnimationFrame(() => textareaRef.current?.focus());
                }}
                disabled={isBusy}
              />
              <button
                onClick={handleSubmit}
                disabled={!canSend}
                className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-white transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:bg-primary"
                aria-label="Send message"
              >
                {isBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ChatInput.displayName = 'ChatInput';

export default ChatInput;
